// 261010 Epic 2 / P3e — CHÍNH SÁCH pass Diễn xuất ở khung mới (thuần: không antd/router, jest chạy được). Phase doc `261010-epic2-phase3e.md` §FE, QĐ 5, 6, 7, 8.
// Quyết: luật nào của hồ sơ phong cách tới Diễn xuất / Đạo diễn · cue nào mỗi shot neo vào (và còn khớp không) · nút "Chạy lại" bật / tắt + nhãn sửa tay · cờ lỗi mềm · lỗi 409 bằng chữ.
// KHÔNG hiện giá (AI chữ, quy ước P2c), KHÔNG có thao tác gỡ dấu sửa tay (Phase doc: không clear-manual ở P3e), KHÔNG đụng quyết định camera (§5).
import type { FilmEvent, FilmShot } from '../../types/film';
import {
  ACTING_MANUAL_FIELD_ORDER, type ActingCue, type ActingFlag, type ActingFlagCode, type ActingManualField, type ActingOutcome, type ActingPlan, type ActingState, type EffectMechanism,
} from '../../types/filmActing';
import type { StyleOwner, StyleProfileView } from '../../types/filmStyleProfile';
import type { WorkflowAlert } from '../../types/filmWorkflow';
import { planWriteKindsOf, planWriteLabelOf, workflowActionError, type ActionError } from './workflowActionPolicy';
import { OWNER_LABEL } from './styleProfilePolicy';

/** Nhãn hiện TRƯỚC nút chạy lại khi video có phần diễn xuất sửa tay. */
export const MANUAL_LABEL = 'sửa tay — AI không ghi đè';
/** Chữ cờ `ACTING_FAILED` (Phase doc §FE) — nguyên văn. */
export const ACTING_FAILED_TEXT = 'Diễn xuất lỗi — giữ shot cũ, đã bỏ qua video này';
export const ACTING_MANUAL_FLAG_TEXT = 'Video này có diễn xuất sửa tay — máy tự chạy bỏ qua, giữ shot cũ';
export const ACTING_FIELD_LABEL: Record<ActingManualField, string> = { performance: 'diễn xuất', motion: 'chuyển động', propChange: 'đồ vật đổi trạng thái', beatKind: 'vai của shot' };
/** Chỉ hai owner luật tới được pass Diễn xuất / Đạo diễn (theo thứ tự hiển thị). */
export const ACTING_OWNERS: StyleOwner[] = ['ACTING', 'DIRECTING'];

/** Ba cờ lỗi mềm / giữ nguyên của chặng 2 liên quan Diễn xuất (BE: alert `code = WORKFLOW_RUN_FLAG:<mã>`, `subjectId` = video). */
const ACTING_FLAG_CODES = ['ACTING_FAILED', 'ACTING_MANUAL_EDIT_EXISTS', 'LEGACY_SHOTS_KEPT'] as const;
const FLAG_PREFIX = 'WORKFLOW_RUN_FLAG:';
/** Chữ cờ `LEGACY_SHOTS_KEPT` (video đã có shot từ trước P3e — chặng 2 giữ nguyên, 0 AI). BE gắn "Video N"; trang chi tiết đã nói rõ video nào nên bỏ số. */
export const LEGACY_SHOTS_KEPT_TEXT = 'Video đã có shot từ trước — giữ nguyên; muốn dựng lại theo diễn xuất thì bấm dựng lại riêng video này';
const trimStr = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

// ───────────────────────── sửa tay ─────────────────────────

/** Hợp `acting_cue_refs.manualFields` của các shot, không trùng, theo thứ tự cố định performance → motion → propChange → beatKind. */
export const manualFieldsOf = (shots: Array<Pick<FilmShot, 'actingCueRefs'>>): ActingManualField[] => {
  const got = new Set<string>(shots.flatMap((s) => s.actingCueRefs?.manualFields ?? []));
  return ACTING_MANUAL_FIELD_ORDER.filter((f) => got.has(f));
};
/** Event được bảo vệ (AI không ghi đè): `acting_plan.status = EDITED` hoặc có shot mang dấu sửa tay. */
export const isActingProtected = (ev: Pick<FilmEvent, 'actingPlan' | 'shots'>): boolean =>
  ev.actingPlan?.status === 'EDITED' || manualFieldsOf(ev.shots ?? []).length > 0;

// ───────────────────────── thẻ luật (ruleCard) ─────────────────────────

export interface RuleRow { id: string; title: string; when: string; then: string; owners: StyleOwner[]; ownerText: string; applied: boolean }
export interface RuleCardView { state: 'LOADING' | 'NONE' | 'DRAFT' | 'APPROVED'; note: string | null; rules: RuleRow[] }

/**
 * Luật của hồ sơ phong cách mà Diễn xuất / Đạo diễn nhận: CHỈ từ bản ĐÃ DUYỆT (nháp không tới bước nào), CHỈ luật có owner ACTING hoặc DIRECTING.
 * `applied` = id có trong `ruleIds` của một cue thật (cái Diễn xuất đã ghi); Đạo diễn không ghi luật nào đã áp nên luật DIRECTING-only luôn `applied: false`.
 */
export function ruleCardOf(view: StyleProfileView | null, plan: ActingPlan | null | undefined): RuleCardView {
  if (!view) return { state: 'LOADING', note: 'Đang tải hồ sơ phong cách…', rules: [] };
  if (view.status === 'NONE' || !view.profile) return { state: 'NONE', note: 'Truyện chưa có hồ sơ phong cách — chưa có luật nào để áp cho Diễn xuất và Đạo diễn.', rules: [] };
  if (view.status === 'DRAFT') return { state: 'DRAFT', note: 'Hồ sơ phong cách chưa duyệt — Diễn xuất và Đạo diễn chưa áp luật nào (duyệt ở bước 2 Tài sản).', rules: [] };
  const used = new Set((plan?.cues ?? []).flatMap((c) => c.ruleIds ?? []));
  const rules = view.profile.rules
    .map((r) => ({ r, owners: ACTING_OWNERS.filter((o) => r.owners.includes(o)) }))
    .filter((x) => x.owners.length > 0)
    .map(({ r, owners }): RuleRow => ({ id: r.id, title: r.title, when: r.when, then: r.then, owners, ownerText: owners.map((o) => OWNER_LABEL[o]).join(' · '), applied: used.has(r.id) }));
  return { state: 'APPROVED', note: rules.length ? null : 'Hồ sơ đã duyệt không có luật nào dành cho Diễn xuất hay Đạo diễn.', rules };
}

// ───────────────────────── cue của shot ─────────────────────────

export type CueRefState = 'NONE' | 'OK' | 'STALE' | 'MISSING';
export interface ShotCues { state: CueRefState; cues: ActingCue[]; missingIds: string[] }

/** Cue của shot theo artifact hiện tại. Shot cũ (không refs / không cueIds) ⇒ `NONE` — không bịa cue. Hash lệch ⇒ `STALE`; id không còn trong artifact (hoặc không có artifact) ⇒ `MISSING`. */
export function resolveShotCues(shot: Pick<FilmShot, 'actingCueRefs'>, plan: ActingPlan | null | undefined): ShotCues {
  const refs = shot.actingCueRefs;
  if (!refs || !refs.cueIds?.length) return { state: 'NONE', cues: [], missingIds: [] };
  const byId = new Map((plan?.cues ?? []).map((c) => [c.id, c]));
  const cues = refs.cueIds.map((id) => byId.get(id)).filter((c): c is ActingCue => !!c);
  const missingIds = refs.cueIds.filter((id) => !byId.has(id));
  if (!plan || missingIds.length) return { state: 'MISSING', cues, missingIds };
  if (refs.actingInputHash !== plan.inputHash) return { state: 'STALE', cues, missingIds };
  return { state: 'OK', cues, missingIds };
}

/** Dòng trace ngắn "Cue c1, c2" (cũ / thiếu thì kèm chữ). Shot cũ không cue ⇒ `null` (thẻ không hiện gì). */
export function cueTraceText(shot: Pick<FilmShot, 'actingCueRefs'>, plan: ActingPlan | null | undefined): string | null {
  const r = resolveShotCues(shot, plan);
  if (r.state === 'NONE') return null;
  const ids = (shot.actingCueRefs?.cueIds ?? []).join(', ');
  if (r.state === 'STALE') return `Cue ${ids} — đã cũ: Diễn xuất đã đổi sau khi dựng shot này`;
  if (r.state === 'MISSING') return `Cue ${ids} — ${r.missingIds.join(', ')} không còn trong Diễn xuất của video`;
  return `Cue ${ids}`;
}

/** Thẻ Quay phim (chỉ đọc): "máy phục vụ cue nào" — mỗi cue còn trong artifact một dòng `id: kích thích → trạng thái sau`. Cue mất bị bỏ, không bịa. */
export function cueServeLines(shot: Pick<FilmShot, 'actingCueRefs'>, plan: ActingPlan | null | undefined): string[] {
  return resolveShotCues(shot, plan).cues.map((c) => [c.id, [trimStr(c.stimulus), trimStr(c.stateShift)].filter(Boolean).join(' → ')].filter(Boolean).join(': '));
}
/** Thẻ Prompt (khối debug): mỗi shot có cue một dòng trace, theo `orderIndex`. */
export function promptCueTrace(ev: Pick<FilmEvent, 'actingPlan' | 'shots'>): Array<{ order: number; text: string }> {
  return [...(ev.shots ?? [])].sort((a, b) => a.orderIndex - b.orderIndex)
    .flatMap((s) => { const text = cueTraceText(s, ev.actingPlan); return text ? [{ order: s.orderIndex, text }] : []; });
}

export interface CueRow { key: string; label: string; text: string }
/** Các dòng của một cue: Kích thích / Phản ứng / Trạng thái sau (đúng mẫu P1) + Diễn xuất + Cách nói + Cơ chế tác động. Ô rỗng bỏ hẳn; cơ chế chỉ hiện khi BE đã giữ lại (BE chỉ cho ở nhịp hành động / phép). */
export function cueRowsOf(cue: ActingCue): CueRow[] {
  const spec: Array<[keyof ActingCue, string]> = [
    ['stimulus', 'Kích thích'], ['physicalResponse', 'Phản ứng'], ['stateShift', 'Trạng thái sau'],
    ['performance', 'Diễn xuất'], ['dialogueDelivery', 'Cách nói'], ['effectMechanism', 'Cơ chế tác động'],
  ];
  return spec.flatMap(([field, label]) => {
    const text = field === 'effectMechanism' ? mechanismText(cue.effectMechanism) : trimStr(cue[field]);
    return text ? [{ key: field, label, text }] : [];
  });
}

const MECHANISM_ORDER: Array<keyof EffectMechanism> = ['source', 'path', 'contact', 'reaction', 'aftermath'];
/** Cơ chế tác động của BE (đối tượng 5 mảnh) ⇒ một dòng "nguồn → đường → tiếp xúc → phản ứng → dư âm"; mảnh trống bỏ; toàn trống / null ⇒ ''. Chuỗi thô (BE cũ) cũng nhận. */
function mechanismText(m: EffectMechanism | string | null | undefined): string {
  if (typeof m === 'string') return m.trim();
  if (!m || typeof m !== 'object') return '';
  return MECHANISM_ORDER.map((k) => trimStr(m[k])).filter(Boolean).join(' → ');
}

// ───────────────────────── chip trạng thái ─────────────────────────

export interface ActingStatus { chip: { label: string; tone: 'ok' | 'warn' | 'doing' | 'muted' } | null; notes: string[] }

/** `state` = `GET …/acting` (tuỳ chọn): `stale` do BE tính (hash đầu vào hiện tại ≠ hash lưu, hoặc đổi bản template), `protected` gồm cả marker chưa kịp có trong payload plan. */
export function actingStatusOf(ev: Pick<FilmEvent, 'actingPlan' | 'shots'>, view: StyleProfileView | null, state?: ActingState | null): ActingStatus {
  const plan = ev.actingPlan;
  if (!plan) return { chip: { label: 'chưa chạy Diễn xuất', tone: 'muted' }, notes: [] };
  const notes: string[] = [];
  const shots = ev.shots ?? [];
  const shotsStale = shots.some((s) => { const st = resolveShotCues(s, plan).state; return st === 'STALE' || st === 'MISSING'; });
  const inputStale = !!state?.stale;
  const stale = shotsStale || inputStale;
  if (shotsStale) notes.push('Diễn xuất đã đổi sau khi dựng một số shot — chạy lại để shot theo bản mới.');
  if (inputStale) notes.push('Đầu vào của Diễn xuất (kịch bản, thoại, hồ sơ phong cách…) đã đổi sau lần viết gần nhất — dựng lại để cập nhật.');
  const approved = view?.status === 'APPROVED' ? view.profile?.approved?.version ?? null : null;
  const drift = approved !== null && plan.profileVersion !== null && plan.profileVersion !== approved;
  if (drift) notes.push(`Diễn xuất chạy theo hồ sơ v${plan.profileVersion}, hồ sơ đã duyệt hiện là v${approved}.`);
  if (isActingProtected(ev) || state?.protected) return { chip: { label: MANUAL_LABEL, tone: 'warn' }, notes };
  if (stale) return { chip: { label: 'Diễn xuất đã cũ', tone: 'warn' }, notes };
  if (drift) return { chip: { label: 'theo hồ sơ cũ', tone: 'warn' }, notes };
  return { chip: { label: 'Diễn xuất khớp', tone: 'ok' }, notes };
}

// ───────────────────────── thanh điều khiển ─────────────────────────

/** `REBUILD` = viết Diễn xuất rồi dàn shot; `DIRECT_ONLY` = artifact đã tươi, chỉ dàn shot (video có shot từ trước, hoặc lần dựng trước bị ngắt giữa chừng). */
export type ActingBarItem =
  | { kind: 'manual-label'; text: string; fields: ActingManualField[] }
  | { kind: 'button'; mode: 'REBUILD' | 'DIRECT_ONLY'; label: string; enabled: boolean; reason?: string };
export interface ActingBarCtx {
  isSky: boolean; eventId: string; ev: Pick<FilmEvent, 'actingPlan' | 'shots'>; jobs: Array<{ type: string; eventId?: string | null; key?: string }>; sending?: boolean;
  /** `GET …/acting` (tuỳ chọn; chưa tải được ⇒ chỉ dựa payload plan). */
  state?: ActingState | null;
}

/**
 * Thứ tự phần tử = thứ tự vẽ: nhãn "sửa tay — AI không ghi đè" LUÔN đứng TRƯỚC nút. Có sửa tay ⇒ nút tắt (BE sẽ trả 409; không có cách gỡ dấu ở P3e).
 * Khoá nút khi plan đang có lượt AI ghi (§7.3: chặng 2 chạy dưới khoá `FILM_WORKFLOW_RUN`, còn nút này dưới `FILM_DIRECT` ⇒ BE không khoá chéo, FE phải khoá).
 * Không xác nhận, không giá (AI chữ). Nút thay shot của video này — chữ giải thích nằm cạnh nút ở thẻ.
 */
export function actingBarOf(ctx: ActingBarCtx): ActingBarItem[] {
  if (!ctx.isSky) return [];
  const items: ActingBarItem[] = [];
  const st = ctx.state ?? null;
  const protectedEv = isActingProtected(ctx.ev) || !!st?.protected;
  const got = new Set<string>([...manualFieldsOf(ctx.ev.shots ?? []), ...(st?.manualShots ?? []).flatMap((m) => m.fields)]);
  const fields = ACTING_MANUAL_FIELD_ORDER.filter((f) => got.has(f));
  if (protectedEv) items.push({ kind: 'manual-label', text: MANUAL_LABEL, fields });
  const kinds = planWriteKindsOf(ctx.jobs as Array<{ type: string }> as never);
  let reason: string | undefined;
  if (protectedEv) reason = `Video này có diễn xuất sửa tay${fields.length ? ` (${fields.map((f) => ACTING_FIELD_LABEL[f]).join(', ')})` : ''} — AI không ghi đè, và bản này chưa có nút gỡ dấu sửa tay nên không dựng lại.`;
  else if (kinds.includes('FILM_WORKFLOW_RUN')) reason = 'Máy tự chạy chặng 2 đang chạy trên plan này — đợi xong rồi mới dựng lại video này.';
  else if (kinds.length) reason = `Đang chạy lượt AI trên cả plan (${kinds.map(planWriteLabelOf).join(', ')}) — đợi xong rồi dựng lại video này.`;
  else if (ctx.sending) reason = 'Đang gửi yêu cầu — đợi một chút.';
  const freshArtifact = !!st && st.status === 'AI' && !st.stale;
  const directOnly = freshArtifact && (st!.shots.length === 0 || st!.shots.some((s) => !s.matches));
  const hasArtifact = !!ctx.ev.actingPlan || (!!st && st.status !== 'NONE');
  items.push({
    kind: 'button', mode: directOnly ? 'DIRECT_ONLY' : 'REBUILD',
    label: directOnly ? 'Dàn shot theo diễn xuất' : hasArtifact ? 'Dựng lại theo diễn xuất' : 'Dựng theo diễn xuất',
    enabled: !reason, ...(reason ? { reason } : {}),
  });
  return items;
}

// ───────────────────────── cờ chặng 2 (lỗi mềm / giữ nguyên) ─────────────────────────

const flagCodeOf = (code: string): string => (code.startsWith(FLAG_PREFIX) ? code.slice(FLAG_PREFIX.length) : code);
/** Alert của BE mang `code = WORKFLOW_RUN_FLAG:<mã>`; mã trần cũng nhận. */
export const isActingFlagAlert = (a: Pick<WorkflowAlert, 'code'>): boolean => (ACTING_FLAG_CODES as readonly string[]).includes(flagCodeOf(a.code));
/** Cờ Diễn xuất của ĐÚNG video (cờ event khác không lẫn sang); `code` đã bỏ tiền tố. */
export const actingFlagsOf = (alerts: WorkflowAlert[], eventId: string): ActingFlag[] =>
  alerts.filter((a) => isActingFlagAlert(a) && a.subjectId === eventId).map((a) => ({ code: flagCodeOf(a.code), eventId: a.subjectId ?? null }));
/** Chữ cờ; mã lạ ⇒ rỗng (mã thô không lộ ra chữ). */
export const actingFlagText = (f: ActingFlag): string => {
  if (f.code === 'ACTING_FAILED') return ACTING_FAILED_TEXT;
  if (f.code === 'ACTING_MANUAL_EDIT_EXISTS') return ACTING_MANUAL_FLAG_TEXT;
  return f.code === 'LEGACY_SHOTS_KEPT' ? LEGACY_SHOTS_KEPT_TEXT : '';
};

/** Cờ tương ứng với từng trạng thái outcome (để không in hai lần cùng một ý). */
const OUTCOME_FLAG: Partial<Record<ActingOutcome['status'], ActingFlagCode>> = { FAILED: 'ACTING_FAILED', MANUAL: 'ACTING_MANUAL_EDIT_EXISTS', LEGACY: 'LEGACY_SHOTS_KEPT' };
const OUTCOME_VIEW: Record<ActingOutcome['status'], { label: string; tone: 'ok' | 'warn' | 'muted' }> = {
  DONE: { label: 'Đã có diễn xuất', tone: 'ok' },
  REUSED: { label: 'Đã có diễn xuất', tone: 'ok' },
  FAILED: { label: 'Diễn xuất lỗi — giữ shot cũ', tone: 'warn' },
  MANUAL: { label: 'Có sửa tay — AI không ghi đè', tone: 'warn' },
  LEGACY: { label: 'Shot có từ trước — giữ nguyên; nút dựng lại riêng video', tone: 'muted' },
};
/** Nhãn của video từ `workflowRuns.stage2.actingOutcomes`; không có (plan cũ / chặng 1 / video không trong danh sách) ⇒ null. Đã có cờ cùng ý ⇒ null (tránh in hai lần). */
export function actingOutcomeOf(outcomes: ActingOutcome[] | undefined, eventId: string, flags: ActingFlag[]): { label: string; tone: 'ok' | 'warn' | 'muted' } | null {
  const o = (outcomes ?? []).find((x) => x.eventId === eventId);
  if (!o) return null;
  const flag = OUTCOME_FLAG[o.status];
  if (flag && flags.some((f) => f.code === flag && (f.eventId ?? eventId) === eventId)) return null;
  return OUTCOME_VIEW[o.status] ?? null;
}

// ───────────────────────── lỗi 409 ─────────────────────────

/** Lỗi Diễn xuất bằng chữ; mã khác ⇒ giao `workflowActionError`. Mã `ACTING_*` lạ (artifact thiếu / cũ…) ⇒ câu chung, không lộ mã. */
export function actingActionError(err: any, what: string): ActionError {
  const d = err?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : '';
  if (code === 'ACTING_MANUAL_EDIT_EXISTS') {
    // BE: `details = { shots: [{shotId, fields}], planEdited }`.
    const shots: unknown[] = Array.isArray(d?.details?.shots) ? d.details.shots : [];
    const raw = shots.flatMap((s) => (Array.isArray((s as { fields?: unknown })?.fields) ? ((s as { fields: unknown[] }).fields) : []));
    const fields = ACTING_MANUAL_FIELD_ORDER.filter((f) => raw.includes(f));
    const which = fields.length ? ` (${fields.map((f) => ACTING_FIELD_LABEL[f]).join(', ')})` : '';
    return { kind: 'EDITED', reload: true, text: `${what}: video này có diễn xuất sửa tay${which} — AI không ghi đè nên không dựng lại; bản này chưa có nút gỡ dấu sửa tay.` };
  }
  if (code === 'ACTING_CHANGED_ELSEWHERE') return { kind: 'CHANGED', reload: true, text: `${what}: video vừa đổi ở nơi khác trong lúc AI chạy — chưa ghi gì, đã tải lại; hãy xem lại rồi dựng lại.` };
  if (code === 'ACTING_NOT_READY') return { kind: 'CHANGED', reload: true, text: `${what}: Diễn xuất của video chưa có hoặc đã cũ (hoặc plan không dùng luồng mới) — viết diễn xuất trước rồi mới dàn shot; đã tải lại.` };
  if (code === 'ACTING_FAILED') return { kind: 'OTHER', reload: true, text: `${what}: AI chưa viết xong diễn xuất — giữ nguyên shot hiện có, thử lại sau.` };
  if (code.startsWith('ACTING_')) return { kind: 'CHANGED', reload: true, text: `${what}: Diễn xuất của video này chưa sẵn sàng hoặc đã đổi — đã tải lại, hãy xem lại rồi chạy lại.` };
  return workflowActionError(err, what);
}

// ───────────────────────── ngoại lệ tập (excPanel) ─────────────────────────

export interface ExceptionPanelView { text: string; editable: false }
/**
 * Ngoại lệ của tập: CHỈ ĐỌC. BE chưa có nơi lưu (dev-be-1 §5.1: dep `loadExceptions` trả `[]`, vẫn được băm) — nên `exceptionHash` luôn có giá trị dù KHÔNG có ngoại lệ nào;
 * vì vậy chỉ ghi mã như "dấu vết đầu vào", KHÔNG khẳng định đã dùng ngoại lệ. FE không bịa API lưu.
 */
export function exceptionPanelOf(plan: ActingPlan | null | undefined): ExceptionPanelView {
  if (!plan) return { text: 'Chưa chạy Diễn xuất nên chưa có ngoại lệ nào được ghi.', editable: false };
  const h = trimStr(plan.exceptionHash);
  return { text: h ? `Mã ngoại lệ tập lúc viết Diễn xuất: ${h.slice(0, 8)} — ngoại lệ tập chưa có nơi lưu nên chưa ngoại lệ nào được dùng.` : 'Diễn xuất chạy không có ngoại lệ của tập.', editable: false };
}
